/**
 * Shared PII (Personally Identifiable Information) hashing utilities.
 * Used by Google Enhanced Conversions and Facebook Conversions API.
 *
 * Works in both Node.js (15+) and browser environments using Web Crypto API.
 * All platforms require SHA256 hashing of PII data with similar normalization rules.
 */

import { getFirstNameAndLastName } from "./text"

/**
 * Creates a SHA256 hash of the input value.
 * Uses the Web Crypto API which is available in both browsers and Node.js 15+.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string}
 */
export async function hashSha256(value: string): Promise<string> {
  const valueUint8Array = new TextEncoder().encode(value)
  const hashBuffer = await crypto.subtle.digest("SHA-256", valueUint8Array)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
}

/**
 * Normalizes an email address.
 * - Converts to lowercase
 * - Trims whitespace
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim()
}

/**
 * Normalizes and hashes an email address.
 * - Converts to lowercase
 * - Trims whitespace
 * - Hashes with SHA256
 */
export async function normalizeAndHashEmail(email: string): Promise<string> {
  return hashSha256(normalizeEmail(email))
}

/**
 * Normalizes and hashes a phone number.
 * - Removes all non-digit characters except leading +
 * - Returns null if input is null/empty
 * - Hashes with SHA256
 */
export async function normalizeAndHashPhone(
  phone: string | null,
): Promise<string | null> {
  if (!phone) return null

  // Remove all non-digit characters, keep leading + for country code
  const normalized = phone.replace(/[^\d+]/g, "").trim()
  if (!normalized) return null

  return hashSha256(normalized)
}

/**
 * Splits a full name into first and last name, normalizes, and hashes each.
 * Uses getFirstNameAndLastName for consistent name splitting across the codebase.
 * - Converts to lowercase for hashing
 * - Trims whitespace
 * - Returns null for missing parts
 */
export async function splitAndHashName(fullName: string | null): Promise<{
  hashedFirstName: string | null
  hashedLastName: string | null
}> {
  const { firstName, lastName } = getFirstNameAndLastName(fullName)

  return {
    hashedFirstName: firstName
      ? await hashSha256(firstName.toLowerCase())
      : null,
    hashedLastName: lastName ? await hashSha256(lastName.toLowerCase()) : null,
  }
}
